import { useCallback, useEffect, useState } from 'react';
import { useApiUrl } from '@refinedev/core';
import { formatDate } from '@/lib/utils';
import { toast } from '@/hooks/useToast';
import { adminFetch } from '@/providers/admin-api';
import { useAdminPermissions } from '@/contexts/AdminPermissionsContext';
import { SortableHeader, type SortOrder } from '@/components/table/SortableHeader';
import {
  Search,
  ChevronLeft,
  ChevronRight,
  ChevronsLeft,
  ChevronsRight,
  Download,
  Filter,
  X,
  ScrollText,
} from 'lucide-react';

/**
 * Audit Log (route: /audit)
 *
 * Compliance surface for "who did what, when?". Fetches
 * GET /v1/admin/audit (requires `audit.read_own`). The backend returns
 * ALL entries to admins with `audit.read_all` and auto-scopes everyone
 * else to their own actions — so the admin_id filter is only meaningful,
 * and only shown, when the caller holds `audit.read_all`.
 *
 * Direct adminFetch (not Refine useList) so the details JSON and the
 * client-side CSV export both work off the exact rows we rendered.
 */
interface AuditEntry {
  id: string;
  action: string;
  entity_type: string;
  entity_id: string | null;
  details: Record<string, unknown> | null;
  ip_address: string | null;
  user_agent: string | null;
  created_at: string;
  admin_email: string | null;
  admin_name: string | null;
}

interface AuditResponse {
  entries: AuditEntry[];
  total: number;
}

const PAGE_SIZE_OPTIONS = [20, 50, 100];

// Closed vocabulary of common entity types the backend logs. Free-text
// input is still allowed via the action filter; entity_type is a dropdown
// for the values an operator most often pivots on.
const ENTITY_TYPE_OPTIONS = [
  'admin',
  'beta_application',
  'admin_setting',
  'promotion',
  'user',
  'role',
];

export function AuditLogPage() {
  const apiUrl = useApiUrl();
  const { hasPermission } = useAdminPermissions();
  const canReadAll = hasPermission('audit.read_all');

  // Filters (committed values used in the fetch).
  const [actionFilter, setActionFilter] = useState('');
  const [entityTypeFilter, setEntityTypeFilter] = useState('');
  const [entityIdFilter, setEntityIdFilter] = useState('');
  const [adminIdFilter, setAdminIdFilter] = useState('');
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');

  const [sortOrder, setSortOrder] = useState<SortOrder>('desc');
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(20);

  const [entries, setEntries] = useState<AuditEntry[]>([]);
  const [total, setTotal] = useState(0);
  const [isLoading, setIsLoading] = useState(true);
  const [isError, setIsError] = useState(false);
  const [expanded, setExpanded] = useState<Set<string>>(new Set());

  const totalPages = Math.ceil(total / pageSize);

  const load = useCallback(async () => {
    setIsLoading(true);
    setIsError(false);
    try {
      const url = new URL(`${apiUrl}/v1/admin/audit`);
      url.searchParams.set('page', String(currentPage));
      url.searchParams.set('limit', String(pageSize));
      url.searchParams.set('sort_order', sortOrder);
      if (actionFilter) url.searchParams.set('action', actionFilter);
      if (entityTypeFilter) url.searchParams.set('entity_type', entityTypeFilter);
      if (entityIdFilter) url.searchParams.set('entity_id', entityIdFilter);
      // admin_id is server-ignored unless caller has audit.read_all, but we
      // only ever send it when the filter is visible.
      if (canReadAll && adminIdFilter) url.searchParams.set('admin_id', adminIdFilter);
      if (startDate) url.searchParams.set('start_date', startDate);
      if (endDate) url.searchParams.set('end_date', `${endDate}T23:59:59`);

      const res = await adminFetch(url.toString());
      const data: AuditResponse = await res.json();
      setEntries(Array.isArray(data.entries) ? data.entries : []);
      setTotal(typeof data.total === 'number' ? data.total : 0);
    } catch {
      setIsError(true);
      setEntries([]);
      setTotal(0);
    } finally {
      setIsLoading(false);
    }
  }, [
    apiUrl,
    currentPage,
    pageSize,
    sortOrder,
    actionFilter,
    entityTypeFilter,
    entityIdFilter,
    adminIdFilter,
    canReadAll,
    startDate,
    endDate,
  ]);

  useEffect(() => {
    load();
  }, [load]);

  // Collapse expanded rows whenever the result set changes.
  useEffect(() => {
    setExpanded(new Set());
  }, [currentPage, pageSize, sortOrder, actionFilter, entityTypeFilter, entityIdFilter, adminIdFilter, startDate, endDate]);

  const activeFilterCount = [
    actionFilter,
    entityTypeFilter,
    entityIdFilter,
    canReadAll ? adminIdFilter : '',
    startDate,
    endDate,
  ].filter(Boolean).length;

  const clearFilters = () => {
    setActionFilter('');
    setEntityTypeFilter('');
    setEntityIdFilter('');
    setAdminIdFilter('');
    setStartDate('');
    setEndDate('');
    setCurrentPage(1);
  };

  const toggleExpand = (id: string) => {
    setExpanded((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const handleSort = () => {
    setSortOrder((o) => (o === 'desc' ? 'asc' : 'desc'));
    setCurrentPage(1);
  };

  // ---- CSV export (client-side, RFC4180 escaping, from fetched rows) ----
  const handleExport = () => {
    try {
      const esc = (v: unknown) => {
        const s = v == null ? '' : String(v);
        return /[",\n]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s;
      };
      const cols: { label: string; value: (r: AuditEntry) => unknown }[] = [
        { label: 'Time', value: (r) => r.created_at },
        { label: 'Admin Email', value: (r) => r.admin_email },
        { label: 'Action', value: (r) => r.action },
        { label: 'Entity Type', value: (r) => r.entity_type },
        { label: 'Entity Id', value: (r) => r.entity_id },
        { label: 'IP', value: (r) => r.ip_address },
        {
          label: 'Details',
          value: (r) => (r.details ? JSON.stringify(r.details) : ''),
        },
      ];
      const header = cols.map((c) => c.label).join(',');
      const rows = entries.map((r) => cols.map((c) => esc(c.value(r))).join(','));
      const csv = [header, ...rows].join('\n');
      const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `audit-log-${new Date().toISOString().split('T')[0]}.csv`;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);
      toast({ title: 'Export Complete', description: 'Audit log exported to CSV' });
    } catch {
      toast({
        title: 'Export Failed',
        description: 'Could not export audit log',
        variant: 'destructive',
      });
    }
  };

  if (isError) {
    return (
      <div className="rounded-lg border border-destructive bg-destructive/10 p-4 text-destructive">
        Failed to load audit log. Please try again.
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="flex items-center gap-2 text-2xl font-bold">
            <ScrollText className="h-6 w-6 text-primary" />
            Audit Log
          </h2>
          <p className="text-muted-foreground">
            History of admin actions — who did what, when, and from where
          </p>
        </div>
        <button
          onClick={handleExport}
          disabled={entries.length === 0}
          className="flex items-center gap-2 rounded-lg bg-primary px-4 py-2 text-sm font-medium text-primary-foreground hover:bg-primary/90 disabled:opacity-50"
        >
          <Download className="h-4 w-4" />
          Export CSV
        </button>
      </div>

      {/* Filters */}
      <div className="flex flex-wrap items-center gap-3">
        <div className="flex items-center gap-2">
          <Filter className="h-4 w-4 text-muted-foreground" />
          <span className="text-sm text-muted-foreground">Filters:</span>
        </div>

        <div className="relative">
          <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
          <input
            type="text"
            placeholder="Action (e.g. delete_user)"
            value={actionFilter}
            onChange={(e) => {
              setActionFilter(e.target.value);
              setCurrentPage(1);
            }}
            aria-label="Filter by action"
            className="h-9 w-56 rounded-lg border bg-background pl-9 pr-3 text-sm focus:border-primary focus:outline-none focus:ring-1 focus:ring-primary"
          />
        </div>

        <select
          value={entityTypeFilter}
          onChange={(e) => {
            setEntityTypeFilter(e.target.value);
            setCurrentPage(1);
          }}
          aria-label="Filter by entity type"
          className="h-9 rounded-lg border bg-background px-3 text-sm focus:border-primary focus:outline-none focus:ring-1 focus:ring-primary"
        >
          <option value="">All entities</option>
          {ENTITY_TYPE_OPTIONS.map((t) => (
            <option key={t} value={t}>
              {t}
            </option>
          ))}
        </select>

        <input
          type="text"
          placeholder="Entity ID"
          value={entityIdFilter}
          onChange={(e) => {
            setEntityIdFilter(e.target.value);
            setCurrentPage(1);
          }}
          aria-label="Filter by entity ID"
          className="h-9 w-40 rounded-lg border bg-background px-3 text-sm focus:border-primary focus:outline-none focus:ring-1 focus:ring-primary"
        />

        {canReadAll && (
          <input
            type="text"
            placeholder="Admin ID"
            value={adminIdFilter}
            onChange={(e) => {
              setAdminIdFilter(e.target.value);
              setCurrentPage(1);
            }}
            aria-label="Filter by admin ID"
            className="h-9 w-40 rounded-lg border bg-background px-3 text-sm focus:border-primary focus:outline-none focus:ring-1 focus:ring-primary"
          />
        )}

        <div className="flex items-center gap-2">
          <label htmlFor="audit-from" className="text-sm text-muted-foreground">
            From:
          </label>
          <input
            id="audit-from"
            type="date"
            value={startDate}
            onChange={(e) => {
              setStartDate(e.target.value);
              setCurrentPage(1);
            }}
            className="h-9 rounded-lg border bg-background px-3 text-sm focus:border-primary focus:outline-none focus:ring-1 focus:ring-primary"
          />
          <label htmlFor="audit-to" className="text-sm text-muted-foreground">
            To:
          </label>
          <input
            id="audit-to"
            type="date"
            value={endDate}
            onChange={(e) => {
              setEndDate(e.target.value);
              setCurrentPage(1);
            }}
            className="h-9 rounded-lg border bg-background px-3 text-sm focus:border-primary focus:outline-none focus:ring-1 focus:ring-primary"
          />
        </div>

        <div className="flex items-center gap-2">
          <label className="text-sm text-muted-foreground">Show:</label>
          <select
            value={pageSize}
            onChange={(e) => {
              setPageSize(Number(e.target.value));
              setCurrentPage(1);
            }}
            aria-label="Items per page"
            className="h-9 rounded-lg border bg-background px-3 text-sm focus:border-primary focus:outline-none focus:ring-1 focus:ring-primary"
          >
            {PAGE_SIZE_OPTIONS.map((size) => (
              <option key={size} value={size}>
                {size}
              </option>
            ))}
          </select>
        </div>

        {activeFilterCount > 0 && (
          <button
            onClick={clearFilters}
            className="flex items-center gap-1 text-sm text-muted-foreground hover:text-foreground"
          >
            <X className="h-3 w-3" />
            Clear ({activeFilterCount})
          </button>
        )}
      </div>

      {/* Table */}
      <div className="rounded-lg border bg-card">
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead>
              <tr className="border-b bg-muted/50">
                <SortableHeader
                  field="created_at"
                  label="Time"
                  activeField="created_at"
                  activeOrder={sortOrder}
                  onSort={handleSort}
                />
                <th className="px-4 py-3 text-left text-sm font-medium text-muted-foreground">
                  Admin
                </th>
                <th className="px-4 py-3 text-left text-sm font-medium text-muted-foreground">
                  Action
                </th>
                <th className="px-4 py-3 text-left text-sm font-medium text-muted-foreground">
                  Entity
                </th>
                <th className="px-4 py-3 text-left text-sm font-medium text-muted-foreground">
                  IP
                </th>
                <th className="px-4 py-3 text-right text-sm font-medium text-muted-foreground">
                  Details
                </th>
              </tr>
            </thead>
            <tbody>
              {isLoading ? (
                Array.from({ length: pageSize }).map((_, index) => (
                  <tr key={`skeleton-${index}`} className="animate-pulse border-b last:border-0">
                    {Array.from({ length: 6 }).map((__, ci) => (
                      <td key={ci} className="px-4 py-3">
                        <div className="h-4 w-20 rounded bg-muted" />
                      </td>
                    ))}
                  </tr>
                ))
              ) : entries.length === 0 ? (
                <tr>
                  <td colSpan={6} className="px-4 py-8 text-center text-muted-foreground">
                    {activeFilterCount > 0
                      ? 'No matching audit entries found'
                      : 'No audit log entries yet'}
                  </td>
                </tr>
              ) : (
                entries.map((entry) => {
                  const isOpen = expanded.has(entry.id);
                  const hasDetails = entry.details && Object.keys(entry.details).length > 0;
                  return (
                    <tr key={entry.id} className="border-b align-top last:border-0 hover:bg-muted/30">
                      <td className="px-4 py-3 text-sm text-muted-foreground whitespace-nowrap">
                        {formatDate(entry.created_at)}
                      </td>
                      <td className="px-4 py-3">
                        {entry.admin_name || entry.admin_email ? (
                          <div>
                            <div className="font-medium">{entry.admin_name || '—'}</div>
                            {entry.admin_email && (
                              <div className="text-xs text-muted-foreground">
                                {entry.admin_email}
                              </div>
                            )}
                          </div>
                        ) : (
                          <span className="text-muted-foreground">—</span>
                        )}
                      </td>
                      <td className="px-4 py-3">
                        <span className="font-medium">{entry.action.replace(/_/g, ' ')}</span>
                      </td>
                      <td className="px-4 py-3">
                        <div className="flex items-center gap-2">
                          <span className="rounded bg-muted px-2 py-0.5 text-xs font-medium">
                            {entry.entity_type || '—'}
                          </span>
                          {entry.entity_id && (
                            <span
                              className="font-mono text-xs text-muted-foreground"
                              title={entry.entity_id}
                            >
                              {entry.entity_id.length > 8
                                ? `${entry.entity_id.substring(0, 8)}…`
                                : entry.entity_id}
                            </span>
                          )}
                        </div>
                      </td>
                      <td className="px-4 py-3 text-sm text-muted-foreground whitespace-nowrap">
                        {entry.ip_address || '—'}
                      </td>
                      <td className="px-4 py-3 text-right">
                        {hasDetails ? (
                          <div className="space-y-2">
                            <button
                              onClick={() => toggleExpand(entry.id)}
                              aria-expanded={isOpen}
                              className="text-sm font-medium text-primary hover:underline"
                            >
                              {isOpen ? 'Hide' : 'View'}
                            </button>
                            {isOpen && (
                              <pre className="max-w-md overflow-x-auto whitespace-pre-wrap break-words rounded-lg border bg-muted/40 p-3 text-left text-xs text-muted-foreground">
                                {JSON.stringify(entry.details, null, 2)}
                              </pre>
                            )}
                          </div>
                        ) : (
                          <span className="text-xs text-muted-foreground">—</span>
                        )}
                      </td>
                    </tr>
                  );
                })
              )}
            </tbody>
          </table>
        </div>

        {/* Pagination */}
        {totalPages > 0 && (
          <div className="flex flex-col gap-3 border-t px-4 py-3 sm:flex-row sm:items-center sm:justify-between">
            <div className="text-sm text-muted-foreground">
              Showing {total === 0 ? 0 : (currentPage - 1) * pageSize + 1} to{' '}
              {Math.min(currentPage * pageSize, total)} of {total} entries
            </div>
            <div className="flex items-center gap-1">
              <button
                onClick={() => setCurrentPage(1)}
                disabled={currentPage === 1}
                className="rounded-lg p-2 text-muted-foreground hover:bg-muted hover:text-foreground disabled:opacity-50"
                title="First page"
              >
                <ChevronsLeft className="h-4 w-4" />
              </button>
              <button
                onClick={() => setCurrentPage((p) => Math.max(1, p - 1))}
                disabled={currentPage === 1}
                className="rounded-lg p-2 text-muted-foreground hover:bg-muted hover:text-foreground disabled:opacity-50"
                title="Previous page"
              >
                <ChevronLeft className="h-4 w-4" />
              </button>
              <span className="px-2 text-sm">
                Page {currentPage} of {totalPages || 1}
              </span>
              <button
                onClick={() => setCurrentPage((p) => Math.min(totalPages, p + 1))}
                disabled={currentPage === totalPages || totalPages === 0}
                className="rounded-lg p-2 text-muted-foreground hover:bg-muted hover:text-foreground disabled:opacity-50"
                title="Next page"
              >
                <ChevronRight className="h-4 w-4" />
              </button>
              <button
                onClick={() => setCurrentPage(totalPages)}
                disabled={currentPage === totalPages || totalPages === 0}
                className="rounded-lg p-2 text-muted-foreground hover:bg-muted hover:text-foreground disabled:opacity-50"
                title="Last page"
              >
                <ChevronsRight className="h-4 w-4" />
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
